import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Box, Button, Divider, TextField, Typography } from '@mui/material';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useHarnessStore } from '@/store/useHarnessStore';
import { harnessService } from '@/services/api/harnessService';

/**
 * Start/Resume Batch controls — both disabled until a device is connected (AC2/Story 1.5 AC1).
 */
export function BatchControls() {
  const connectionState = useDeviceStore((s) => s.connectionState);
  const deviceInfo = useDeviceStore((s) => s.deviceInfo);
  const activeBatch = useHarnessStore((s) => s.activeBatch);
  const setActiveBatch = useHarnessStore((s) => s.setActiveBatch);

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Synchronous re-entrancy guard: the `isStarting` state only disables the button
  // after an async re-render, so two clicks in the same tick could both fire a request
  // and create duplicate batches. This ref blocks the second call immediately.
  const isStartingRef = useRef(false);

  // Pre-fill the Batch ID from a `?resume=` param so the "Add measurements (Resume)" affordance in
  // the read-only review view (Story 3.1) lands here with the Batch already filled — the user only
  // has to reconnect the matching device. Read once (lazy initialiser); the field stays editable.
  const [searchParams] = useSearchParams();
  const [resumeBatchId, setResumeBatchId] = useState(() => searchParams.get('resume') ?? '');
  const [isResuming, setIsResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const isResumingRef = useRef(false);

  const isConnected = connectionState === 'connected';

  const handleStartBatch = async () => {
    if (!deviceInfo) return;
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    setIsStarting(true);
    setError(null);
    try {
      const batch = await harnessService.startBatch({
        deviceUid: deviceInfo.uid,
        firmwareVersion: deviceInfo.firmwareVersion,
        hardwareVersion: deviceInfo.hardwareVersion,
        serialNumber: deviceInfo.serialNumber,
      });
      setActiveBatch(batch);
    } catch {
      setError('Failed to start Batch. Please try again.');
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  };

  const handleResumeBatch = async () => {
    if (!deviceInfo) return;
    if (!resumeBatchId.trim()) return;
    if (isResumingRef.current) return;
    isResumingRef.current = true;

    setIsResuming(true);
    setResumeError(null);
    try {
      const { batch, measurements } = await harnessService.resumeBatch(resumeBatchId.trim(), {
        deviceUid: deviceInfo.uid,
        firmwareVersion: deviceInfo.firmwareVersion,
        hardwareVersion: deviceInfo.hardwareVersion,
        serialNumber: deviceInfo.serialNumber,
      });
      setActiveBatch(batch, measurements);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 409) {
        setResumeError('This Batch was started with a different device — resume is blocked.');
      } else if (status === 404) {
        setResumeError('Batch not found.');
      } else if (status === 400) {
        // A non-GUID Batch ID fails server-side model binding with a 400 — surface it as an
        // input problem rather than the generic "try again", which would mislead the user.
        setResumeError('That is not a valid Batch ID.');
      } else {
        setResumeError('Failed to resume Batch. Please try again.');
      }
    } finally {
      isResumingRef.current = false;
      setIsResuming(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button
        variant="contained"
        disabled={!isConnected || isStarting}
        onClick={handleStartBatch}
      >
        {isStarting ? 'Starting Batch…' : 'Start Batch'}
      </Button>

      {!isConnected && (
        <Typography variant="body2" color="text.secondary">
          Connect a device to start a Batch.
        </Typography>
      )}

      {activeBatch && (
        <Typography variant="body2" color="text.secondary">
          Active Batch: {activeBatch.id}
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Divider />

      <Typography variant="subtitle2">Resume a past Batch</Typography>
      <TextField
        label="Batch ID"
        size="small"
        value={resumeBatchId}
        onChange={(e) => setResumeBatchId(e.target.value)}
        disabled={!isConnected || isResuming}
      />
      <Button
        variant="outlined"
        disabled={!isConnected || isResuming || !resumeBatchId.trim()}
        onClick={handleResumeBatch}
      >
        {isResuming ? 'Resuming Batch…' : 'Resume Batch'}
      </Button>

      {resumeError && <Alert severity="error">{resumeError}</Alert>}
    </Box>
  );
}
